// API Response Types - Aligned with REST API Documentation

import type { ID, ISODateString } from '@/types/common';

// Base API Response Structure (per documentation)
export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  pagination?: PaginationInfo;
}

export interface ApiError {
  message: string;
  errors?: Array<{
    field: string;
    message: string;
  }>;
}

export interface PaginationInfo {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

// User Management Types
export interface UserProfile {
  _id: ID;
  email: string;
  username: string;
  fullname?: string;
  phone?: string;
  role: string; // Role ID
  roleName?: string; // Role name for display
  roleId: string;
  isActive: boolean;
  createdAt: ISODateString;
  lastLogin?: ISODateString;
  updatedAt: ISODateString;
}

export interface UserData {
  _id: ID;
  email: string;
  username: string;
  fullname?: string;
  phone?: string;
  invitedBy?: string;
  isActive: boolean;
  permissions: UserPermission[];
  role: string; // Role ID
  roleName?: string; // Role name for display
  roleId?: string;
  createdAt: ISODateString;
  lastLogin?: ISODateString;
  updatedAt: ISODateString;
}

// Legacy User type for backward compatibility
export interface User extends UserData {}

export interface UserPermission {
  _id: ID;
  resource: string;
  actions: string[];
}

// API Response Types for User endpoints
export interface GetUserProfileResponse
  extends ApiResponse<{ user: UserProfile }> {}

export interface GetUsersResponse
  extends ApiResponse<{
    users: UserData[];
    pagination: PaginationInfo;
  }> {}

export interface GetUserResponse extends ApiResponse<{ user: UserData }> {}

export interface UserSearchResponse
  extends ApiResponse<{
    users: UserData[];
    pagination: PaginationInfo;
  }> {}

export interface BulkUserUpdateRequest {
  userIds: string[];
  updateData: {
    isActive?: boolean;
    role?: string;
    roleId?: string;
  };
}

export interface BulkUserUpdateResponse
  extends ApiResponse<{
    total: number;
    successful: number;
    failed: number;
    details: Array<{
      userId: string;
      status: 'success' | 'error';
      error?: string;
    }>;
  }> {}

// RBAC Types
export interface Permission {
  _id: string;
  resource: string;
  actions: string[];
}

export interface Role {
  _id: ID;
  name: string;
  description?: string;
  permissions: Permission[];
  isActive: boolean;
  permissionCount?: number;
  createdAt: ISODateString;
  updatedAt: ISODateString;
}

// RBAC API Response Types
export interface GetRolesResponse extends ApiResponse<{ roles: Role[] }> {}

export interface GetRoleHierarchyResponse
  extends ApiResponse<{
    hierarchy: Role[];
  }> {}

export interface GetUserPermissionsResponse
  extends ApiResponse<{
    permissions: Permission[];
  }> {}

export interface GetUsersWithPermissionResponse
  extends ApiResponse<{
    users: Array<{
      _id: string;
      firstName?: string;
      lastName?: string;
      email: string;
      roleName: string;
      role: string;
    }>;
  }> {}

export interface PermissionStatistics {
  totalRoles: number;
  totalUsers: number;
  superAdmins: number;
  roleDistribution: Record<string, number>;
  permissionUsage: Record<string, Record<string, number>>;
}

export interface GetPermissionStatisticsResponse
  extends ApiResponse<{
    stats: PermissionStatistics;
  }> {}

// Role management request/response types
export interface CreateRoleRequest {
  name: string;
  description?: string;
  permissions: Array<{
    resource: string;
    actions: string[];
  }>;
  isActive?: boolean;
}

export interface UpdateRoleRequest {
  roleId: string;
  name?: string;
  description?: string;
  permissions?: Array<{
    resource: string;
    actions: string[];
  }>;
  isActive?: boolean;
}

export interface UpdateUserPermissionsRequest {
  userId: string;
  roleId?: string;
  permissions?: Array<{
    resource: string;
    actions: string[];
  }>;
}

export interface RevokeUserPermissionsRequest {
  userId: string;
  permissionKeys: string[];
}

export interface BulkUpdateUserPermissionsRequest {
  updates: Array<{
    userId: string;
    roleId?: string;
    permissions?: Permission[];
  }>;
}

export interface BulkUpdateUserPermissionsResponse
  extends ApiResponse<{
    results: Array<{
      userId: string;
      success: boolean;
      error?: string;
    }>;
  }> {}

// User creation/update form types
export interface UserCreateFormData {
  email: string;
  username: string;
  fullname?: string;
  roleId: string;
}

export interface UserUpdateFormData {
  email: string;
  username: string;
  fullname?: string;
  phone?: string;
  roleId: string;
  isActive: boolean;
}

// Advanced user search parameters
export interface UserSearchParams {
  email?: string;
  username?: string;
  fullname?: string;
  roleName?: string;
  isActive?: boolean;
  createdAfter?: string;
  createdBefore?: string;
  lastLoginAfter?: string;
  lastLoginBefore?: string;
  page?: number;
  limit?: number;
  sortBy?:
    | 'createdAt'
    | 'updatedAt'
    | 'email'
    | 'fullname'
    | 'roleName'
    | 'lastLogin';
  sortOrder?: 'asc' | 'desc';
}

// Resource and action constants
export const AVAILABLE_RESOURCES = [
  'all',
  'sales_order',
  'purchase_order',
  'discounts',
  'regions',
  'inventory',
  'suppliers',
  'dashboard',
  'reports',
  'customers',
  'requisitions',
  'audit',
  'notifications',
  'transactions',
  'users',
] as const;

export const AVAILABLE_ACTIONS = [
  'read',
  'write',
  'update',
  'delete',
  'approve',
] as const;

export type ResourceName = (typeof AVAILABLE_RESOURCES)[number];
export type ActionName = (typeof AVAILABLE_ACTIONS)[number];

// Permission utility types
export interface PermissionCheck {
  resource: ResourceName;
  action: ActionName;
}

export interface EffectivePermissions {
  rolePermissions: Permission[];
  directPermissions: Permission[];
  combinedPermissions: Permission[];
}
